import React from 'react'
import { Field, useFormikContext } from 'formik'
import { Autocomplete } from '@carsys/form'
import { IRevenueGroupAdministration, Ledger } from '@cbgms/api'
import { Alert, AlertTitle, Grid, ListItem, Paper, Popper, PopperProps, Typography } from '@mui/material'
import { sortLedgersOnVATType } from './utils'
import { useEnumTranslation } from '@cbgms/base/enums/use-enum-translation'
import { StructuredDepartments } from './types.d'
import { useTranslation } from 'react-i18next'
import { EmptyUUID } from '@cbgms/base/utils/uuid'

interface IProps {
  tabIndex: number
  departments: StructuredDepartments
}

interface LedgerWithArrayIndex extends Ledger {
  arrayIndex: number
}

interface IStructuredLedgersForDepartment {
  name: string
  uuid: string
  ledgers: LedgerWithArrayIndex[]
}

type StructuredLedgersForDepartments = IStructuredLedgersForDepartment[]

const Departments: React.FC<IProps> = ({ tabIndex, departments }) => {
  const { t } = useTranslation()
  const { te } = useEnumTranslation()
  const {
    values: { Administrations, VatLimiter },
    setFieldValue
  } = useFormikContext<IRevenueGroupAdministration>()

  const autocompleteLedgerDefaultProps = {
    freeSolo: true,
    autoSelect: true,
    options:
      Administrations[tabIndex].LedgerAccounts?.map(item => {
        return { text: item.Code, value: item.Code, label: `${item.Code} ${item.Description}` }
      }) || [],
    renderOption: (props: any, option: any) => (
      <ListItem key={props.key}>{option?.label || option?.text || option?.value || option}</ListItem>
    ),
    PopperComponent: (props: PopperProps) => {
      return (
        <Popper {...props} placement='bottom-start' style={{ width: 'fit-content', minWidth: props?.style?.width, maxWidth: '80vh' }} />
      )
    }
  }

  const getStructuredLedgersForDepartments = () => {
    const structuredLedgers: StructuredLedgersForDepartments = []

    departments.forEach(department => {
      const ledgersPerDepartment = Administrations[tabIndex].Ledgers.map((ledger, index) => {
        // We add arrayIndex to have the correct order of fetched items in the array.
        const ledgerWithArrayIndex = { ...ledger, arrayIndex: index }
        return ledgerWithArrayIndex
      })
        .filter(ledger => ledger.DepartmentUUID === department.uuid)
        .sort(sortLedgersOnVATType)

      structuredLedgers.push({
        name: department.name,
        uuid: department.uuid,
        ledgers: ledgersPerDepartment
      })
    })

    return structuredLedgers
  }

  const setValidationOnFields = (type: keyof Ledger, value: string, ledger: LedgerWithArrayIndex) => {
    if (ledger.DepartmentUUID !== EmptyUUID && ledger.DepartmentUUID !== '' && ledger.AdministrationUUID === EmptyUUID) {
      for (let adminIndex = 0; adminIndex < Administrations.length; adminIndex++) {
        for (let ledgerIndex = 0; ledgerIndex < Administrations[adminIndex].Ledgers.length; ledgerIndex++) {
          const indexedLedger = Administrations[adminIndex].Ledgers[ledgerIndex]
          const indexedFallbackLedger = Administrations[adminIndex].Ledgers.find(
            ledger => ledger.DepartmentUUID === EmptyUUID || ledger.DepartmentUUID === ''
          )

          const allDefaultLedger = Administrations.find(Administration => Administration.UUID === EmptyUUID)?.Ledgers.find(
            ledgers => ledgers.DepartmentUUID === EmptyUUID || ledgers.DepartmentUUID === ''
          )

          if (
            Administrations[adminIndex].UUID !== EmptyUUID &&
            ledger.DepartmentUUID === indexedLedger.DepartmentUUID &&
            indexedLedger.VatType === ledger.VatType
          ) {
            if (type === 'Revenue') {
              const isRevenueFieldRequired = !allDefaultLedger?.Revenue && !indexedFallbackLedger?.Revenue && !value

              setFieldValue(`Administrations[${adminIndex}].Ledgers[${ledgerIndex}].IsRevenueFieldRequired`, isRevenueFieldRequired)
            } else if (type === 'Discount') {
              const isDiscountFieldRequired = !allDefaultLedger?.Discount && !indexedFallbackLedger?.Discount && !value

              setFieldValue(`Administrations[${adminIndex}].Ledgers[${ledgerIndex}].IsDiscountFieldRequired`, isDiscountFieldRequired)
            }
          }
        }
      }
    }
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {getStructuredLedgersForDepartments().map((department, index) => (
          <Grid key={`department-${index}`} item xs={6}>
            <Grid item xs={12}>
              <Typography variant='h6'>{department.name}</Typography>
            </Grid>
            <Grid container spacing={1} p={2}>
              {VatLimiter.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity='warning'>
                    <AlertTitle>{t('revenue_group.no_vat_type_selected')}</AlertTitle>
                    {t('revenue_group.select_vat_type')}
                  </Alert>
                </Grid>
              )}
              {department.ledgers.map(ledger => (
                <Grid container item key={ledger.arrayIndex} spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant='subtitle1'>{te('EnumVatType', ledger.VatType)}</Typography>
                  </Grid>
                  <Grid container item spacing={2} xs={12}>
                    <Field
                      name={`Administrations[${tabIndex}].Ledgers[${ledger.arrayIndex}].DepartmentUUID`}
                      value={ledger.DepartmentUUID}
                      type='hidden'
                    />
                    <Grid item xs={6}>
                      <Autocomplete
                        name={`Administrations[${tabIndex}].Ledgers[${ledger.arrayIndex}].Revenue`}
                        required={Administrations[tabIndex].Ledgers[ledger.arrayIndex].IsRevenueFieldRequired}
                        label={t('revenue_group.revenue')}
                        onChange={(value: string) => {
                          setValidationOnFields('Revenue', value, ledger)
                        }}
                        {...autocompleteLedgerDefaultProps}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Autocomplete
                        name={`Administrations.${tabIndex}.Ledgers.${ledger.arrayIndex}.Discount`}
                        required={Administrations[tabIndex].Ledgers[ledger.arrayIndex].IsDiscountFieldRequired}
                        label={t('revenue_group.discount')}
                        onChange={(value: string) => {
                          setValidationOnFields('Discount', value, ledger)
                        }}
                        {...autocompleteLedgerDefaultProps}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Paper>
  )
}

export default Departments
